import { useState } from 'react'
import type { FormEvent } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'
import { useFriends } from '@/hooks/useFriends'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'

const getDeleteFriendErrorMessage = (error: { code?: string; message?: string; details?: string }) => {
  const combined = `${error.message ?? ''} ${error.details ?? ''}`.toLowerCase()

  if (error.code === '23503' || combined.includes('foreign key')) {
    return "You can't delete a friend who has books assigned to them"
  }

  return error.message ?? 'Failed to remove friend'
}

export const Friends = () => {
  const { user } = useAuth()
  const { friends, loading, error, addFriend, removeFriend } = useFriends(user?.id)
  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleAddFriend = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSubmitting(true)

    const { error } = await addFriend(name.trim())

    if (error) {
      toast.error(error.message)
    } else {
      toast.success('Friend added')
      setName('')
    }

    setSubmitting(false)
  }

  const handleDelete = async (friendId: number) => {
    const { error } = await removeFriend(friendId)

    if (error) {
      toast.error(getDeleteFriendErrorMessage(error))
      return
    }

    toast.success('Friend removed')
  }

  return (
    <main className='mx-auto w-full max-w-5xl p-3 md:p-6'>
      <Card>
        <CardHeader>
          <CardTitle>Your friends</CardTitle>
          <CardDescription>Friends are private to your own account.</CardDescription>
        </CardHeader>
        <CardContent className='space-y-3'>
          <form onSubmit={handleAddFriend}>
            <div className='flex items-center gap-2'>
              <Input
                id='friend-name'
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder='Friend name'
                required
              />
              <Button
                type='submit'
                size='sm'
                disabled={submitting || !name.trim()}
                className='h-10 w-10 p-0'
                aria-label='Add friend'
              >
                {submitting ? '...' : <Plus className='h-4 w-4' />}
              </Button>
            </div>
          </form>

          {loading && <p className='text-sm text-muted-foreground'>Loading...</p>}
          {error && <p className='text-sm text-destructive'>{error}</p>}
          {!loading && friends.length === 0 && (
            <p className='text-sm text-muted-foreground'>No friends added yet.</p>
          )}
          {!loading && friends.length > 0 && (
            <div className='overflow-hidden rounded-md border'>
              <table className='w-full text-sm'>
                <thead className='bg-muted/50'>
                  <tr>
                    <th className='px-3 py-2 text-left font-medium'>Name</th>
                    <th className='w-16 px-3 py-2 text-right font-medium'></th>
                  </tr>
                </thead>
                <tbody>
                  {friends.map((friend) => (
                    <tr key={friend.id} className='border-t'>
                      <td className='px-3 py-2 font-medium'>{friend.name}</td>
                      <td className='px-3 py-2 text-right'>
                        <Button
                          type='button'
                          variant='ghost'
                          size='sm'
                          className='h-8 w-8 p-0 text-muted-foreground hover:text-destructive'
                          onClick={() => void handleDelete(friend.id)}
                          aria-label={`Remove ${friend.name}`}
                        >
                          <Trash2 className='h-4 w-4' />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </main>
  )
}
